/**
 * Page 1 of Form SS-4 (Rev. December 2025), replicated.
 *
 * Layout is absolute rather than flowed: the official is a fixed-layout page,
 * so matching it means placing things, not nudging them. Every x/y below is a
 * point coordinate measured out of fss4.pdf, and every rule and checkbox comes
 * from ss4-rules.ts, which was extracted from the same render.
 *
 * The bold/regular switches mid-sentence are not stylistic — the official sets
 * body copy in HelveticaNeue Roman 8pt and switches to Bd/It for the phrases
 * marked here.
 *
 * ---------------------------------------------------------------------------
 * Three places where these numbers are NOT the official's
 * ---------------------------------------------------------------------------
 * Both are deliberate, and both are about the sheet being read on screen and
 * on a printed capture rather than admired as a facsimile.
 *
 * 1. Checkboxes take the y of the run they read with, not their own measured
 *    top. The official sets a box like a character on the baseline — 8pt tall
 *    on 8pt type — so it reaches 2.3pt above the caps and stops level with the
 *    baseline, which reads high, with more white under it than over it. `Cb`
 *    now centres itself on the middle of the run's x-height; see the note on
 *    it in
 *    ss4-layout.tsx.
 *
 * 2. Rows whose descenders landed on the rule beneath them are lifted. The
 *    official runs these genuinely tight: on the "Real estate / Manufacturing"
 *    row the 8pt descenders stop 0.6pt short of the rule at y=600 — fine on a
 *    1200dpi proof, touching once the sheet is rasterised and printed. The
 *    lift is
 *    1.5pt for 8pt rows and 1.0pt for 7pt, which clears the rule below by
 *    ~1.4pt while leaving the checkbox ~1.4pt under the rule above; it is as
 *    much as the 12pt rows have room for. Boxes follow their run, so they stay
 *    centred.
 *
 * 3. Tall cells have their slack shared out instead of pooled at the bottom.
 *    The official hangs a 24pt cell's caption 1.9pt under the rule above it and
 *    leaves 4.2pt under the value below, so the caption crowds the rule while
 *    the space that would have fixed it sits unused lower down. Moving caption
 *    and value together evens the three gaps -- 2.9 / 3.0 / 3.2 on lines 1-7,
 *    and the same treatment on lines 11-12, 15 and the Third Party Designee
 *    block. Nothing is squeezed to pay for it; the cell is just used evenly.
 *
 *    Write-in values are left alone: a value sitting 1.7pt over its own dotted
 *    rule is a value sitting ON its line, which is correct, not crowding.
 */
import { At, Arrow, Cb, Field, Leader, RULE_W, Rules, pt, FONT_WORDMARK, FONT_TITLE } from "./ss4-layout";
import { H_RULES_P1, V_RULES_P1 } from "./ss4-rules";

export type SS4Value = any;

interface SS4PageProps {
  value?: SS4Value;
  onChange?: (patch: any) => void;
  disabled?: boolean;
}

const SS4Page = ({ value, onChange, disabled }: SS4PageProps) => {
  const v = value || {};
  const llc = v?.llc_extracted_data || {};
  const ss4 = v?.ss4_extracted_data || {};
  const set =
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
      onChange?.({ [name]: e.target.value });

  // ---- derived values, carried over from the previous components ----------
  const fullname =
    `${v?.extracted_data?.name || ""} ${v?.extracted_data?.surname || ""}`.trim();
  const companyMember = `${v?.company_members || ""}`;
  const companyType = Number(v?.company_type || 1);

  const cityStateZip =
    `${llc?.city || ""}, ${llc?.state || ""} ${llc?.zip_code || ""}`.trim();
  const countryStates = [llc?.city, llc?.state].filter(Boolean).join(", ");

  const isPartnership = companyType === 1 && companyMember !== "Single Member";
  const isOtherEntity = companyType === 1 && companyMember === "Single Member";
  const isCorporation = companyType === 2;

  const llcMembers =
    companyType === 1 && companyMember === "Single Member"
      ? "1"
      : companyMember === "Multimember"
        ? String(1 + (v?.additional_members?.length || 0))
        : companyType === 2
          ? ""
          : v?.employees_count || "";

  const businessType = isOtherEntity
    ? v?.industry_type_name === "Other"
      ? v?.business_description || "Other"
      : v?.industry_type_name || ""
    : companyType === 2
      ? "CORPORATION"
      : "LLC PARTNERSHIP";

  // Line 17 is always the specific (sub) industry.
  const merchandise =
    v?.industry_type_name === "Other"
      ? v?.business_description || "Other"
      : v?.industry_type_name || "";
  // Line 16's "Other (specify)" is the broad sector for everything except
  // Single Member LLCs (left blank) and Multimember LLCs, which show the
  // same sub industry as line 17 instead of the parent industry.
  const principalActivity = isOtherEntity
    ? ""
    : isPartnership
      ? merchandise
      : v?.industry_type_name === "Other"
        ? v?.business_description || "Other"
        : v?.industry_parent_name || v?.industry_type_name || "";

  const nameAndTitle = fullname;

  return (
    <>
      <Rules
        h={H_RULES_P1}
        v={V_RULES_P1}
        /* the handful the official draws heavier than a 0.5pt hairline:
           the masthead's OMB rule and divider at 1pt, the Third Party
           Designee box at 0.75, and the footer rule at 1 */
        hWeights={{ 84: 1, 708: 0.75, 756: 1 }}
        vWeights={{ 439.2: 1, 86: 0.75 }}
      />

      {/* ============================ masthead ============================ */}
      <At x={36} y={46} size={7} fit={16.3}>Form</At>
      <At
        x={54.2}
        y={30}
        size={24}
        fit={47.2}
        leading={24}
        style={{ fontFamily: FONT_WORDMARK, fontWeight: 400 }}
      >
        SS-4
      </At>
      <At x={36} y={56} size={7} fit={69.5}>(Rev. December 2025)</At>
      <At x={36} y={68} size={7} fit={76.4}>Department of the Treasury</At>
      <At x={36} y={74} size={7} fit={69.1}>Internal Revenue Service</At>

      <At x={131} y={33} size={14} fit={292.4} leading={14} style={{ fontFamily: FONT_TITLE }}>
        Application for Employer Identification Number
      </At>
      <At x={132} y={47} size={8} bold fit={290.8}>
        (For use by employers, corporations, partnerships, trusts, estates,
        churches,
      </At>
      <At x={134} y={55} size={8} bold fit={286.1}>
        government agencies, Indian tribal entities, certain individuals, and
        others.)
      </At>
      <At x={145} y={65} size={8} bold fit={264.5}>
        See separate instructions for each line. Keep a copy for your records.
      </At>
      <At x={142} y={73} size={8} bold>
        Go to <span style={{ fontStyle: "italic" }}>www.irs.gov/FormSS4</span>{" "}
        for instructions and the latest information.
      </At>

      <At x={475} y={37} size={7} fit={65.1}>OMB No. 1545-0003</At>
      <At x={444} y={48} size={7} bold fit={12.0}>EIN</At>
      <Field
        x={442}
        y={57}
        w={132}
        h={24}
        name="ein"
        value={v?.ein || ""}
        onChange={set("ein")}
        disabled={disabled}
      />

      {/* ===================== "Type or print clearly." ==================== */}
      {/* Rotated in the 14.4pt gutter beside lines 1-7 (y 84 -> 228). */}
      <div
        style={{
          position: "absolute",
          left: pt(36),
          top: pt(84),
          width: pt(14.4),
          height: pt(144),
          overflow: "hidden",
        }}
      >
        <span
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) rotate(-90deg)",
            whiteSpace: "nowrap",
            fontSize: pt(10),
            lineHeight: 1,
            fontWeight: 700,
          }}
        >
          Type or print clearly.
        </span>
      </div>

      {/* ========================== lines 1 - 7 =========================== */}
      <At x={54} y={85} size={8} bold>1</At>
      <At x={74} y={85} size={8} fit={255.3}>
        Legal name of entity (or individual) for whom the EIN is being requested
      </At>
      <Field
        x={52}
        y={95}
        w={522}
        h={13}
        name="preferred_name_1"
        value={llc?.company_name || ""}
        onChange={set("preferred_name_1")}
        disabled={disabled}
        maxLength={50}
      />

      <At x={54} y={109} size={8} bold>2</At>
      <At x={74} y={109} size={8} fit={201.6}>
        Trade name of business (if different from name on line 1)
      </At>
      <Field
        x={52}
        y={119}
        w={241}
        h={13}
        name="trade_name"
        value={llc?.trade_name || ""}
        onChange={set("trade_name")}
        disabled={disabled}
        maxLength={50}
      />
      <At x={299} y={109} size={8} bold>3</At>
      <At x={317} y={109} size={8} fit={170.7}>
        Executor, administrator, trustee, “care of” name
      </At>
      <Field x={297} y={119} w={277} h={13} disabled maxLength={50} />

      <At x={54} y={133} size={8} bold>4a</At>
      <At x={75} y={133} size={8} fit={218}>
        Mailing address (room, apt., suite no. and street, or P.O. box)
      </At>
      <Field
        x={52}
        y={143}
        w={241}
        h={13}
        name="address"
        value={llc?.business_address || ""}
        onChange={set("address")}
        disabled={disabled}
        maxLength={50}
      />
      <At x={299} y={133} size={8} bold>5a</At>
      <At x={317} y={133} size={8} fit={183.4}>
        Street address (if different) (Don’t enter a P.O. box.)
      </At>
      <Field x={297} y={143} w={277} h={13} disabled maxLength={50} />

      <At x={54} y={157} size={8} bold>4b</At>
      <At x={75} y={157} size={8} fit={186.7}>
        City, state, and ZIP code (if foreign, see instructions)
      </At>
      <Field
        x={52}
        y={167}
        w={241}
        h={13}
        name="cityStateZip"
        value={cityStateZip}
        onChange={set("cityStateZip")}
        disabled={disabled}
        maxLength={50}
      />
      <At x={299} y={157} size={8} bold>5b</At>
      <At x={317} y={157} size={8} fit={187.3}>
        City, state, and ZIP code (if foreign, see instructions)
      </At>
      <Field x={297} y={167} w={277} h={13} disabled />

      <At x={54} y={181} size={8} bold>6</At>
      <At x={74} y={181} size={8} fit={189.2}>
        County and state where principal business is located
      </At>
      <Field
        x={52}
        y={191}
        w={522}
        h={13}
        name="countryState"
        value={countryStates}
        readOnly
        disabled={disabled}
        maxLength={50}
      />

      <At x={54} y={205} size={8} bold>7a</At>
      <At x={75} y={205} size={8} fit={93.9}>Name of responsible party</At>
      <Field x={52} y={215} w={277} h={13} value={fullname} readOnly disabled />
      <At x={335} y={205} size={8} bold>7b</At>
      <At x={351} y={205} size={8} fit={62}>SSN, ITIN, or EIN</At>
      <Field x={333} y={215} w={241} h={13} value="foreign" readOnly disabled />

      {/* ============================= 8a / 8b ============================ */}
      <At x={36} y={229} size={8} bold>8a</At>
      <At x={58} y={229} size={8} fit={194.0}>
        Is this application for a limited liability company (LLC)
      </At>
      <At x={58} y={240.5} size={8}>
        (or a foreign equivalent)? <Leader n={8} />
      </At>
      <Cb x={255} y={240.5} checked={companyType === 1} />
      <At x={267} y={240.5} size={8} fit={13.3}>Yes</At>
      <Cb x={298.25} y={240.5} checked={companyType === 2} />
      <At x={310} y={240.5} size={8}>No</At>

      <At x={336} y={229} size={8} bold>8b</At>
      <At x={353} y={229} size={8} fit={136.6}>If 8a is “Yes,” enter the number of</At>
      <At x={353} y={240} size={8}>
        LLC members <Leader n={7} />
      </At>
      <Field x={468} y={239} w={106} h={11} value={llcMembers} readOnly />

      {/* =============================== 8c =============================== */}
      <At x={36} y={252.5} size={8} bold>8c</At>
      <At x={58} y={252.5} size={8}>
        If 8a is “Yes,” was the LLC organized in the United States?{" "}
        <Leader n={20} />
      </At>
      <Cb x={492.5} y={252.5} checked={companyType === 1} />
      <At x={504} y={252.5} size={8} fit={13.9}>Yes</At>
      <Cb x={543} y={252.5} checked={companyType !== 1 && companyType !== 2} />
      <At x={555} y={252.5} size={8}>No</At>

      {/* =============================== 9a =============================== */}
      <At x={36} y={266} size={8} bold>9a</At>
      <At x={58} y={266} size={8} fit={390.2}>
        <b>Type of entity</b> (check only one box). <b>Caution:</b> If 8a is
        “Yes,” see the instructions for the correct box to check.
      </At>

      <Cb x={60.5} y={278} />
      <At x={73} y={278} size={8} fit={76}>Sole proprietor (SSN)</At>
      <Field x={158.2} y={278} w={101.3} h={10} disabled style={{ borderBottom: RULE_W }} />
      <Cb x={334.25} y={278} />
      <At x={347} y={278} size={8} fit={89.9}>Estate (SSN of decedent)</At>
      <Field x={446} y={278} w={130} h={10} disabled style={{ borderBottom: RULE_W }} />

      <Cb x={60.5} y={290} checked={isPartnership} />
      <At x={73} y={290} size={8} fit={41.2}>Partnership</At>
      <Cb x={334.25} y={290} />
      <At x={347} y={290} size={8} fit={83.7}>Plan administrator (TIN)</At>
      <Field x={439} y={290} w={137} h={10} disabled style={{ borderBottom: RULE_W }} />

      <Cb x={60.5} y={302} checked={isCorporation} />
      <At x={73} y={302} size={8} fit={153.6}>Corporation (enter form number to be filed)</At>
      <Field
        x={237.3}
        y={302}
        w={87}
        h={10}
        value={isCorporation ? "1120" : ""}
        readOnly
        style={{ borderBottom: RULE_W }}
      />
      <Cb x={334.25} y={302} />
      <At x={347} y={302} size={8} fit={74}>Trust (TIN of grantor)</At>
      <Field x={431.8} y={302} w={144} h={10} disabled style={{ borderBottom: RULE_W }} />

      <Cb x={60.5} y={314} />
      <At x={73} y={314} size={8} fit={102.4}>Personal service corporation</At>
      <Cb x={334.25} y={314} />
      <At x={347} y={314} size={8} fit={82.2}>Military/National Guard</At>
      <Cb x={442.25} y={314} />
      <At x={455} y={314} size={8} fit={82.9}>State/local government</At>

      <Cb x={60.5} y={326} />
      <At x={73} y={326} size={8} fit={147.5}>Church or church-controlled organization</At>
      <Cb x={334.25} y={326} />
      <At x={347} y={326} size={8} fit={71.9}>Farmers’ cooperative</At>
      <Cb x={442.25} y={326} />
      <At x={455} y={326} size={8} fit={71.4}>Federal government</At>

      <Cb x={60.5} y={338} />
      <At x={73} y={338} size={8} fit={132.5}>Other nonprofit organization (specify)</At>
      <Field x={215.8} y={338} w={108.5} h={10} disabled style={{ borderBottom: RULE_W }} />
      <Cb x={334.25} y={338} />
      <At x={347} y={338} size={8} fit={25.0}>REMIC</At>
      <Cb x={442.25} y={338} />
      <At x={455} y={338} size={8} fit={119.6}>Indian tribal governments/enterprises</At>

      <Cb x={60.5} y={348.5} checked={isOtherEntity} />
      <At x={73} y={348.5} size={8} fit={52.1}>Other (specify)</At>
      <Field
        x={130}
        y={350}
        w={194}
        h={10}
        name="typeEntityText"
        value={isOtherEntity ? v?.typeEntityText || "DISREGARDED ENTITY" : ""}
        readOnly
        disabled
      />
      <At x={331} y={348.5} size={8} fit={138.6}>Group Exemption Number (GEN) if any</At>
      <Field x={478} y={350} w={98} h={10} disabled />

      {/* =============================== 9b =============================== */}
      <At x={36} y={362} size={8} bold>9b</At>
      <At x={58} y={361} size={8} fit={185.7}>
        If a corporation, name the state or foreign country (if
      </At>
      <At x={58} y={372} size={8} fit={110.3}>applicable) where incorporated</At>
      <At x={278} y={360} size={8} fit={18.4}>State</At>
      <Field
        x={276}
        y={370}
        w={125}
        h={13}
        name="state"
        value={llc?.state || ""}
        onChange={set("state")}
        disabled={disabled}
      />
      <At x={407} y={360} size={8} fit={56.3}>Foreign country</At>
      <Field
        x={405}
        y={370}
        w={169}
        h={13}
        name="foreignCountry"
        value={v?.foreignCountry || ""}
        onChange={set("foreignCountry")}
        disabled={disabled}
      />

      {/* =============================== 10 =============================== */}
      <At x={36} y={386} size={8} bold>10</At>
      <At x={58} y={386} size={8} fit={152}>
        <b>Reason for applying</b> (check only one box)
      </At>
      <Cb x={276.5} y={386} />
      <At x={289} y={386} size={8} fit={124.7}>Banking purpose (specify purpose)</At>
      <Field x={424.5} y={386} w={151.5} h={10} disabled style={{ borderBottom: RULE_W }} />

      <Cb x={60.5} y={398} checked />
      <At x={73} y={398} size={8} fit={127}>Started new business (specify type)</At>
      <Field x={208.5} y={398} w={58} h={10} value="" readOnly style={{ borderBottom: RULE_W }} />
      <Cb x={276.5} y={398} />
      <At x={289} y={398} size={8} fit={172.5}>
        Changed type of organization (specify new type)
      </At>
      <Field x={467.8} y={398} w={108} h={10} disabled style={{ borderBottom: RULE_W }} />

      {/* the continuation rule under "Started new business" carries the type */}
      <Field
        x={57.3}
        y={410}
        w={209}
        h={10}
        value={businessType}
        readOnly
        style={{ borderBottom: RULE_W }}
      />
      <Cb x={276.5} y={410} />
      <At x={289} y={410} size={8} fit={94.7}>Purchased going business</At>

      <Cb x={60.5} y={422} />
      <At x={73} y={422} size={8} fit={177.4}>
        Hired employees (Check the box and see line 13.)
      </At>
      <Cb x={276.5} y={422} />
      <At x={289} y={422} size={8} fit={103.3}>Created a trust (specify type)</At>
      <Field x={403} y={422} w={173} h={10} disabled style={{ borderBottom: RULE_W }} />

      <Cb x={60.5} y={434} />
      <At x={73} y={434} size={8} fit={160.1}>
        Compliance with IRS withholding regulations
      </At>
      <Cb x={276.5} y={434} />
      <At x={289} y={434} size={8} fit={133}>Created a pension plan (specify type)</At>
      <Field x={431.8} y={434} w={144} h={10} disabled style={{ borderBottom: RULE_W }} />

      <Cb x={60.5} y={444.5} />
      <At x={73} y={444.5} size={8} fit={52.1}>Other (specify)</At>
      <Field x={130} y={446} w={194} h={10} disabled />

      {/* ============================= 11 / 12 ============================ */}
      <At x={36} y={457.5} size={8} bold>11</At>
      <At x={58} y={458.5} size={8} fit={251.1}>
        Date business started or acquired (month, day, year). See instructions.
      </At>
      <Field
        x={52}
        // 468.5, not 466.5: an 8pt gap under the label left no room for the
        // descenders of "day, year". Every other field on the sheet clears its
        // label by 10pt, and that slack is what absorbs the ~1.3pt of vertical
        // drift between how macOS and Windows read this font's metrics
        // (see utils/formFontMetrics.ts). At 8pt there was none, so the label
        // was clipped on macOS and half-hidden on Windows and Ubuntu.
        y={468.5}
        w={276}
        // The cell is 456-480 and the label needs 10pt of clearance, so the
        // box can start at 468.5 and must end by 480: h = 11.5. With the
        // input's 1.5pt top padding that leaves 10pt of content, which an 8pt
        // line box fits on every platform. h=10 did not -- it left 8.5pt and
        // Windows clipped the descenders of the value itself.
        h={11.5}
        name="createdAt"
        // line 11 sits in its own boxed cell, so the official centres the date
        // rather than running it against the left rule
        style={{ textAlign: "center" }}
        value={llc?.started_date || ""}
        onChange={set("createdAt")}
        disabled={disabled}
        placeholder="mm/dd/yyyy"
        maxLength={10}
      />
      <At x={337} y={457.5} size={8} bold>12</At>
      <At x={354} y={457.5} size={8} fit={120.2}>Closing month of accounting year</At>
      {/* Moves with line 11 above: they share a row, and a 2pt difference
          between them would read as misalignment. */}
      <Field x={333} y={468.5} w={241} h={11.5} value="December" readOnly />

      {/* ============================= 13 / 14 ============================ */}
      <At x={36} y={482} size={8} bold>13</At>
      <At x={58} y={483} size={8} fit={271.0}>
        Highest number of employees expected in the next 12 months (enter -0- if
        none).
      </At>
      <At x={58} y={494} size={8} fit={130.5}>
        If no employees expected, skip line 14.
      </At>
      <At x={81} y={516} size={8} fit={40}>Agricultural</At>
      <Field x={38} y={526} w={104} h={13} value="0" readOnly style={{ textAlign: "center" }} />
      <At x={168} y={516} size={8} fit={38.5}>Household</At>
      <Field x={146} y={526} w={82} h={13} value="0" readOnly style={{ textAlign: "center" }} />
      <At x={271} y={516} size={8} fit={19.8}>Other</At>
      <Field x={232} y={526} w={97} h={13} value="0" readOnly style={{ textAlign: "center" }} />

      <At x={337} y={483} size={8} bold>14</At>
      <At x={351} y={483} size={8} fit={214.3}>
        If you expect your employment tax liability to be $1,000 or less in a
      </At>
      <At x={351} y={492} size={8} fit={223.9}>
        full calendar year <b>and</b> want to file Form 944 annually instead of
        Forms
      </At>
      <At x={351} y={502} size={8} fit={223.4}>
        941 quarterly, check here. (Your employment tax liability will generally
      </At>
      <At x={351} y={511} size={8} fit={222.6}>
        be $1,000 or less if you expect to pay $5,000 or less, $6,536 or less if
      </At>
      <At x={351} y={521} size={8} fit={217.8}>
        you're in a U.S. territory, in total wages.) If you don't check this
        box,
      </At>
      <At x={351} y={528.5} size={8}>
        you must file Form 941 for every quarter <Leader n={7} />
      </At>
      <Cb x={563.25} y={528.5} checked={(v?.liabilityCheck ?? ss4?.liabilityCheck) === true} />

      {/* =============================== 15 =============================== */}
      <At x={36} y={541} size={8} bold>15</At>
      <At x={58} y={541} size={8} fit={518.0}>
        First date wages or annuities were paid (month, day, year).{" "}
        <b>Note:</b> If applicant is a withholding agent, enter date income will
        first be paid to
      </At>
      <At x={58} y={552} size={8}>
        nonresident alien (month, day, year) <Leader n={20} />
      </At>
      <Field
        x={400}
        y={551}
        w={174}
        h={10}
        name="firstDate"
        value={v?.firstDate || ss4?.firstDate || ""}
        onChange={set("firstDate")}
        disabled={disabled}
        placeholder="mm/dd/yyyy"
        maxLength={10}
      />

      {/* =============================== 16 =============================== */}
      <At x={36} y={566} size={8} bold>16</At>
      <At x={58} y={566} size={8} fit={249.3}>
        Check <b>one</b> box that best describes the principal activity of your
        business.
      </At>
      <Cb x={319.75} y={566} />
      <At x={332} y={566} size={8} fit={106.1}>Health care &amp; social assistance</At>
      <Cb x={449.25} y={566} />
      <At x={462} y={566} size={8} fit={90.8}>Wholesale—agent/broker</At>

      <Cb x={60.5} y={578} />
      <At x={73} y={578} size={8} fit={41.7}>Construction</At>
      <Cb x={125.25} y={578} />
      <At x={138} y={578} size={8} fit={57.5}>Rental &amp; leasing</At>
      <Cb x={204.5} y={578} />
      <At x={217} y={578} size={8} fit={91.4}>Transportation &amp; warehousing</At>
      <Cb x={319.75} y={578} />
      <At x={332} y={578} size={8} fit={107.1}>Accommodation &amp; food service</At>
      <Cb x={449.25} y={578} />
      <At x={462} y={578} size={8} fit={63.4}>Wholesale—other</At>
      <Cb x={535.75} y={578} />
      <At x={548} y={578} size={8} fit={20.5}>Retail</At>

      <Cb x={60.5} y={588.5} />
      <At x={73} y={588.5} size={8} fit={40.2}>Real estate</At>
      <Cb x={125.25} y={588.5} />
      <At x={138} y={588.5} size={8} fit={51.6}>Manufacturing</At>
      <Cb x={204.5} y={588.5} />
      <At x={217} y={588.5} size={8} fit={72.5}>Finance &amp; insurance</At>
      <Cb x={319.75} y={588.5} checked />
      <At x={332} y={588.5} size={8} fit={52.3}>Other (specify)</At>
      <Field
        x={392}
        y={590}
        w={184}
        h={10}
        name="otherReason"
        value={principalActivity}
        readOnly
        disabled={disabled}
      />

      {/* =============================== 17 =============================== */}
      <At x={36} y={602} size={8} bold>17</At>
      <At x={58} y={602} size={8} fit={421.4}>
        Indicate principal line of merchandise sold, specific construction work
        done, products produced, or services provided.
      </At>
      <Field
        x={56}
        // 612, not 610: same 8pt-gap problem as line 11 -- the descenders of
        // "principal" and "produced" ran into the box.
        y={612}
        w={518}
        // Cell is 600-624, label clears at 612, so h = 12 fills the rest
        // exactly. Leaves 10.5pt of content after the top padding.
        h={12}
        name="industry_type_name"
        value={merchandise}
        readOnly
        disabled={disabled}
      />

      {/* =============================== 18 =============================== */}
      <At x={36} y={626} size={8} bold>18</At>
      <At x={58} y={626} size={8} fit={278.9}>
        Has the applicant entity shown on line 1 ever applied for and received
        an EIN?
      </At>
      <Cb x={355.75} y={626} />
      <At x={368} y={626} size={8} fit={13.9}>Yes</At>
      <Cb x={399} y={626} checked />
      <At x={412} y={626} size={8}>No</At>
      <At x={58} y={636.5} size={8} fit={114.0}>If “Yes,” write previous EIN here</At>
      <Field x={180} y={637} w={160} h={10} disabled />

      {/* ====================== Third Party Designee ====================== */}
      <At x={36} y={660} size={9} bold fit={22.2}>Third</At>
      <At x={36} y={671} size={9} bold fit={22.5}>Party</At>
      <At x={36} y={682} size={9} bold fit={40.2}>Designee</At>

      <At x={90} y={648.5} size={8} fit={485.3}>
        Complete this section <b>only</b> if you want to authorize the named
        individual to receive the entity’s EIN and answer questions about the
        completion of this form.
      </At>

      <At x={90} y={661} size={8} fit={62.1}>Designee’s name</At>
      <Field x={88} y={671} w={341} h={13} disabled />
      <At x={434} y={662} size={7} fit={142.4}>
        Designee’s telephone number (include area code)
      </At>
      <Field x={434} y={672} w={140} h={12} disabled />

      <At x={90} y={685} size={8} fit={80.3}>Address and ZIP code</At>
      <Field x={88} y={695} w={341} h={13} disabled />
      <At x={434} y={686} size={7} fit={133.3}>
        Designee’s fax number (include area code)
      </At>
      <Field x={434} y={696} w={140} h={12} disabled />

      {/* ===================== perjury + signature ======================== */}
      <At x={36} y={710} size={7} fit={393.1}>
        Under penalties of perjury, I declare that I have examined this
        application, and to the best of my knowledge and belief, it is true,
        correct, and complete.
      </At>
      <At x={36} y={722} size={7} fit={111.2}>Name and title (type or print clearly)</At>
      <Field
        x={150}
        y={722}
        w={278}
        h={10}
        name="nameAndTitle"
        value={nameAndTitle}
        readOnly
      />
      <At x={434} y={708} size={8} fit={142.3}>
        Applicant’s telephone number (include area code)
      </At>
      <Field
        x={434}
        y={718}
        w={140}
        h={13}
        name="phone_number"
        value={
          v?.phone_number
            ? `${v?.country_code || "+1"} ${v.phone_number}`
            : ""
        }
        onChange={set("phone_number")}
        disabled={disabled}
      />

      <At x={36} y={745} size={7} fit={30.0}>Signature</At>
      <At x={331} y={745} size={7} fit={14.8}>Date</At>
      <At x={434} y={732} size={8} fit={122.6}>
        Applicant’s fax number (include area code)
      </At>
      <Field
        x={434}
        y={742}
        w={140}
        h={13}
        value={v?.faxNumber || "+1 302-319-4259"}
        readOnly
        disabled
      />

      {/* ============================= footer ============================= */}
      <At x={36} y={759.5} size={8} bold fit={305.2}>
        For Privacy Act and Paperwork Reduction Act Notice, see separate
        instructions.
      </At>
      <At x={359} y={760.5} size={7} fit={52.4}>Cat. No. 16055N</At>
      <At x={430} y={760.5} size={7} fit={16.1}>Form</At>
      <At x={449.1} y={757.5} size={10} bold leading={10} fit={22.4}>
        SS-4
      </At>
      <At x={473.8} y={760.5} size={7} fit={102.2}>(Rev. 12-2025) Created 10/17/25</At>
    </>
  );
};

export default SS4Page;
